/// Port of mobile/lib/game/sky/star.dart.
///
/// Stars drift at their own fixed px/s rather than a fraction of game speed,
/// unlike clouds. That is deliberate in the original.

import { Component } from '../engine/component';
import type { Rng } from '../engine/random';
import { defaultRng } from '../engine/random';
import { SpriteComponent, sprite } from '../engine/sprite';
import type { Vec2 } from '../engine/vector';
import { worldWidth } from '../world';
import { skyAssets } from './skyAssets';

export type StarKind = 'bright' | 'normal' | 'dim';

const starKinds: readonly StarKind[] = ['bright', 'normal', 'dim'];

function starAsset(kind: StarKind): { path: string; dim: Vec2 } {
  switch (kind) {
    case 'bright':
      return { path: skyAssets.starBright, dim: { x: 11, y: 11 } };
    case 'normal':
      return { path: skyAssets.star, dim: { x: 9, y: 9 } };
    case 'dim':
      return { path: skyAssets.starDim, dim: { x: 9, y: 9 } };
  }
}

interface AmbientDeps {
  allowsAmbientMotion: () => boolean;
  rng?: Rng;
}

/// Slow-drifting night sky star.
export class Star extends SpriteComponent {
  readonly kind: StarKind;
  readonly driftSpeed: number;

  private readonly deps: AmbientDeps;

  constructor(options: { position: Vec2; kind: StarKind; driftSpeed: number }, deps: AmbientDeps) {
    const asset = starAsset(options.kind);
    super({
      position: options.position,
      size: { ...asset.dim },
      priority: -1,
      sprite: sprite(asset.path, asset.dim),
    });
    this.deps = deps;
    this.kind = options.kind;
    this.driftSpeed = options.driftSpeed;
  }

  override update(dt: number): void {
    if (!this.deps.allowsAmbientMotion()) return;
    this.x -= this.driftSpeed * dt;
  }
}

/// Scatters stars and keeps a few drifting across the night sky.
export class StarManager extends Component {
  static readonly initialCount = 12;
  static readonly maxCount = 16;
  static readonly minDrift = 4.0;
  static readonly maxDrift = 14.0;
  static readonly skyTop = 4.0;
  static readonly skyBottom = 90.0;

  private readonly rng: Rng;
  private readonly deps: AmbientDeps;

  constructor(deps: AmbientDeps) {
    super(-1);
    this.deps = deps;
    this.rng = deps.rng ?? defaultRng;
    this.scatterInitial();
  }

  private scatterInitial(): void {
    for (let i = 0; i < StarManager.initialCount; i += 1) {
      this.add(this.spawnStar(this.rng.nextDouble() * worldWidth));
    }
  }

  override update(_dt: number): void {
    if (!this.deps.allowsAmbientMotion()) return;

    for (const star of this.childrenOfType(Star)) {
      if (star.x + star.width < 0) {
        star.removeFromParent();
      }
    }

    const stars = this.childrenOfType(Star);
    if (stars.length < StarManager.maxCount) {
      const rightmost = stars.reduce(
        (best, star) => Math.max(best, star.x),
        Number.NEGATIVE_INFINITY,
      );
      if (rightmost < worldWidth - 40 || stars.length === 0) {
        this.add(this.spawnStar(worldWidth + this.rng.nextDouble() * 80));
      }
    }
  }

  private spawnStar(x: number): Star {
    const kind = starKinds[this.rng.nextInt(starKinds.length)];
    const y =
      StarManager.skyTop + this.rng.nextDouble() * (StarManager.skyBottom - StarManager.skyTop);
    const driftSpeed =
      StarManager.minDrift + this.rng.nextDouble() * (StarManager.maxDrift - StarManager.minDrift);

    return new Star({ position: { x, y }, kind, driftSpeed }, this.deps);
  }

  reset(): void {
    this.removeWhere((child) => child instanceof Star);
    for (let i = 0; i < StarManager.initialCount; i += 1) {
      this.add(this.spawnStar(this.rng.nextDouble() * worldWidth));
    }
  }
}
